import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Pagination,
  Button,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  LinearProgress as LinearProgressIcon,
} from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import axios from 'axios';

// Helper function to check if file is HEIC
const isHeicFile = (fileName, fileExt = null) => {
  if (fileExt) {
    const ext = fileExt.toLowerCase().replace(/^\./, '');
    return ext === 'heic' || ext === 'heif';
  }
  if (!fileName) return false;
  const ext = fileName.toLowerCase().split('.').pop();
  return ext === 'heic' || ext === 'heif';
};

// Helper function to check if file is TIFF (browsers can't display it, so it is converted for preview)
const isTiffFile = (fileName, fileExt = null) => {
  const ext = (fileExt || fileName?.split('.').pop() || '').toLowerCase().replace(/^\./, '');
  return ext === 'tif' || ext === 'tiff';
};

// Files that get a server-converted JPEG preview
const isConvertedPreviewFile = (fileName, fileExt = null) =>
  isHeicFile(fileName, fileExt) || isTiffFile(fileName, fileExt);

const LibraryPage = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [businessProfile, setBusinessProfile] = useState(null);
  const [library, setLibrary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [currentFolder, setCurrentFolder] = useState(null); // Track current folder ID
  const [folderStack, setFolderStack] = useState([]); // Track folder path with names
  const [uploading, setUploading] = useState(false); // Track upload state
  const [uploadProgress, setUploadProgress] = useState(0); // Track upload progress
  const [videoPreviewUrls, setVideoPreviewUrls] = useState({}); // Store video thumbnails
  const [videoLoadingStates, setVideoLoadingStates] = useState({}); // Track video loading states
  const [heicPreviewUrls, setHeicPreviewUrls] = useState({}); // Store HEIC converted previews
  const [heicLoadingStates, setHeicLoadingStates] = useState({}); // Track HEIC loading states
  const [statusEditing, setStatusEditing] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = React.useRef(null); // Reference to file input
  const itemsPerPage = 12;

  const STATUS_OPTIONS = [
    { value: 'new', label: 'New', color: '#2196f3' },
    { value: 'pending', label: 'Pending', color: '#ff9800' },
    { value: 'uploaded', label: 'Uploaded', color: '#4caf50' },
    { value: 'approved', label: 'Approved', color: '#8bc34a' },
    { value: 'rejected', label: 'Rejected', color: '#f44336' }
  ];

  // NOTE: the old "navigate when selectedBusiness differs" guard was removed -
  // it was a workaround for the missing 'library' mapping in getCurrentFeature
  // and fought the context's URL-based auto-select (URL kept snapping back).
  // Both sync directions are handled centrally in BusinessSearchContext now.

  // When locationId changes, reset folder state and fetch new library
  useEffect(() => {
    setCurrentFolder(null);
    setFolderStack([]);
    setPage(1);
    setLibrary(null);
    setSelectedItem(null);
    setPreviewOpen(false);
  }, [locationId]);

  // Fetch library when locationId or currentFolder changes
  useEffect(() => {
    fetchLibrary();
  }, [locationId, currentFolder]);


  // Convert HEIC files for preview
  useEffect(() => {
    const convertHeicFiles = async () => {
      if (!library || library.length === 0) return;

      const folders = library.filter(item => item.isFolder);
      const files = library.filter(item => !item.isFolder);
      const allItems = [...folders, ...files];
      const startIdx = (page - 1) * itemsPerPage;
      const currentPageItems = allItems.slice(startIdx, startIdx + itemsPerPage);

      const newUrls = { ...heicPreviewUrls };
      const newLoadingStates = { ...heicLoadingStates };
      let hasChanges = false;

      for (const item of currentPageItems) {
        if (item.isFolder) continue;
        if (!isConvertedPreviewFile(item.name, item.details?.extension)) continue;

        const itemId = item._id;
        if (newUrls[itemId] || newLoadingStates[itemId]) continue;

        const fileUrl = `${item.url}/${item.key}`;
        newLoadingStates[itemId] = true;
        hasChanges = true;

        try {
          console.log(`🔄 Converting HEIC preview for ${item.name}...`);
          const response = await fetch('/api/convert-heic-preview', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ fileUrl })
          });

          if (response.ok) {
            const result = await response.json();
            console.log(`HEIC conversion response:`, result);
            if (result.success && (result.dataUrl || result.thumbnailUrl)) {
              console.log(`✅ HEIC converted for ${item.name}`);
              newUrls[itemId] = result.dataUrl || result.thumbnailUrl;
            } else {
              console.warn(`⚠️ No dataUrl in HEIC response:`, result);
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.warn(`⚠️ HEIC conversion HTTP ${response.status}:`, errorData);
          }
        } catch (err) {
          console.error(`❌ HEIC conversion error for ${item.name}:`, err.message);
        } finally {
          newLoadingStates[itemId] = false;
        }
      }

      if (hasChanges) {
        setHeicPreviewUrls(newUrls);
        setHeicLoadingStates(newLoadingStates);
      }
    };

    convertHeicFiles();
  }, [library, page]);

  // Extract video thumbnails only for current page with parallel requests
  useEffect(() => {
    const extractVideoThumbnails = async () => {
      if (!library || library.length === 0) return;

      // Calculate pagination inside effect to avoid initialization issues
      const folders = library.filter(item => item.isFolder);
      const files = library.filter(item => !item.isFolder);
      const allItems = [...folders, ...files];
      const startIdx = (page - 1) * itemsPerPage;
      const currentPageItems = allItems.slice(startIdx, startIdx + itemsPerPage);

      // Get videos that need extraction
      const videosToExtract = currentPageItems.filter(item => {
        if (item.isFolder) return false;
        const fileType = item.details?.fileType || '';
        if (!fileType.startsWith('video/')) return false;
        const itemId = item._id;
        return !videoPreviewUrls[itemId] && !videoLoadingStates[itemId];
      });

      if (videosToExtract.length === 0) return;

      const newUrls = { ...videoPreviewUrls };
      const newLoadingStates = { ...videoLoadingStates };

      // Mark all as loading
      videosToExtract.forEach(item => {
        newLoadingStates[item._id] = true;
      });
      setVideoLoadingStates(newLoadingStates);

      // Parallel extraction with concurrency limit (4 at a time)
      const concurrencyLimit = 4;
      const extractPromises = videosToExtract.map(item => async () => {
        const itemId = item._id;
        const fileUrl = item.url;
        const fileKey = item.key;
        if (!fileUrl || !fileKey) return;

        const fullFileUrl = `${fileUrl}/${fileKey}`;

        try {
          const response = await fetch('/api/extract-video-thumbnail', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ fileUrl: fullFileUrl })
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.thumbnailUrl) {
              newUrls[itemId] = result.thumbnailUrl;
            }
          }
        } catch (err) {
          console.warn(`⚠️ Failed to extract thumbnail for ${item.name}`);
        }
      });

      // Execute with concurrency limit
      for (let i = 0; i < extractPromises.length; i += concurrencyLimit) {
        const batch = extractPromises.slice(i, i + concurrencyLimit);
        await Promise.all(batch.map(fn => fn()));
      }

      // Update state once
      const finalLoadingStates = { ...videoLoadingStates };
      videosToExtract.forEach(item => {
        finalLoadingStates[item._id] = false;
      });
      setVideoLoadingStates(finalLoadingStates);
      setVideoPreviewUrls(newUrls);
    };

    extractVideoThumbnails();
  }, [library, page]);

  const fetchLibrary = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use cached profile if available
      let profile = businessProfile;
      if (!profile) {
        const profileResponse = await axios.get(
          `/api/businesses/${locationId}/details`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            },
            timeout: 8000
          }
        );
        profile = profileResponse.data;
      }

      if (!profile?.contactCloudTenantId) {
        setError('Business profile does not have Contact Cloud Tenant ID configured');
        setLibrary([]);
        setLoading(false);
        return;
      }

      setBusinessProfile(profile);

      // Fetch library with timeout
      let libraryUrl = `/api/businesses/${locationId}/library?sortMode=descending&limit=60&page=1`;
      if (currentFolder) {
        libraryUrl += `&parent=${currentFolder}`;
      }

      const libraryResponse = await axios.get(libraryUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        timeout: 10000
      });

      const documents = libraryResponse.data.rows?.rows?.documents || [];
      setLibrary(documents);
      setError(null);
    } catch (err) {
      let errorMsg = 'Failed to fetch library';
      if (err.response?.status === 401) {
        errorMsg = 'Authentication failed';
      } else if (err.response?.status === 403) {
        errorMsg = 'Access denied';
      } else if (err.code === 'ECONNABORTED') {
        errorMsg = 'Request timeout';
      }
      setError(errorMsg);
      setLibrary([]);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (item) => {
    if (item.isFolder) {
      return <FolderIcon sx={{ fontSize: 40, color: '#FF9800' }} />;
    }

    const fileType = item.details?.fileType || '';
    const extension = item.details?.extension || '';

    if (fileType.startsWith('image/')) {
      return <ImageIcon sx={{ fontSize: 40, color: '#2196F3' }} />;
    } else if (fileType.startsWith('video/')) {
      return <VideoIcon sx={{ fontSize: 40, color: '#F44336' }} />;
    } else if (fileType.includes('pdf') || fileType.includes('document')) {
      return <DocumentIcon sx={{ fontSize: 40, color: '#9C27B0' }} />;
    }
    return <FileIcon sx={{ fontSize: 40, color: '#757575' }} />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const b = parseInt(bytes);
    if (b === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(b) / Math.log(k));
    return Math.round((b / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'uploaded': '#4caf50',
      'pending': '#ff9800',
      'new': '#2196f3',
      'approved': '#8bc34a',
      'rejected': '#f44336'
    };
    return colors[status?.toLowerCase()] || '#9e9e9e';
  };


  const findMediaMappingFileId = (fileName) => {
    if (!businessProfile?.mediaMapping?.folders) return null;

    for (const folder of businessProfile.mediaMapping.folders) {
      if (folder.images) {
        for (const image of folder.images) {
          if (image.name === fileName) {
            return image._id?.toString();
          }
        }
      }
      if (folder.folders) {
        const found = searchSubfolders(folder.folders, fileName);
        if (found) return found;
      }
    }
    return null;
  };

  const searchSubfolders = (folders, fileName) => {
    for (const folder of folders) {
      if (folder.images) {
        for (const image of folder.images) {
          if (image.name === fileName) {
            return image._id?.toString();
          }
        }
      }
      if (folder.folders) {
        const found = searchSubfolders(folder.folders, fileName);
        if (found) return found;
      }
    }
    return null;
  };

  const updateFileStatus = async (fileName, newStatus) => {
    try {
      setStatusUpdating(true);

      const mediaMappingFileId = findMediaMappingFileId(fileName);
      if (!mediaMappingFileId) {
        setSnackbar({
          open: true,
          message: 'Datei nicht in Medien-Datenbank gefunden',
          severity: 'warning'
        });
        setStatusUpdating(false);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/folders-with-metadata/${locationId}/${mediaMappingFileId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        console.log(`✅ Updated file status to: ${newStatus}`);
        setSnackbar({
          open: true,
          message: `Status erfolgreich aktualisiert zu: ${newStatus}`,
          severity: 'success'
        });
        setStatusEditing(false);
        setSelectedStatus(null);

        // Refresh businessProfile to get updated mediaMapping
        try {
          const token = localStorage.getItem('auth_token');
          const profileResponse = await axios.get(
            `/api/businesses/${locationId}/details`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              },
              timeout: 8000
            }
          );
          setBusinessProfile(profileResponse.data);
          console.log('✅ BusinessProfile refreshed with updated status');
        } catch (err) {
          console.warn('⚠️ Failed to refresh profile:', err.message);
        }
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      console.error('Error updating file status:', err);
      setSnackbar({
        open: true,
        message: `Fehler: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setStatusUpdating(false);
    }
  };

  const getFileStatus = (fileIdOrName) => {
    if (!businessProfile?.mediaMapping?.folders) {
      return 'new';
    }

    // Recursively search through folders for the file
    const findFileStatus = (folders) => {
      for (const folder of folders) {
        // Check images in current folder
        if (folder.images) {
          for (const image of folder.images) {
            // Match by ID or by name (name is the fallback)
            if (image._id === fileIdOrName || image.name === fileIdOrName) {
              return image.status || 'new';
            }
          }
        }
        // Recursively check subfolders
        if (folder.folders && folder.folders.length > 0) {
          const result = findFileStatus(folder.folders);
          if (result) return result;
        }
      }
      return null;
    };

    return findFileStatus(businessProfile.mediaMapping.folders) || 'new';
  };

  const openFolder = (folder) => {
    // Push current folder to stack
    setFolderStack([...folderStack, { id: currentFolder, name: currentFolder === null ? 'Library' : 'Back' }]);
    setCurrentFolder(folder._id);
    setPage(1);
  };

  const goBack = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const previousFolder = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolder(previousFolder.id);
      setPage(1);
    }
  };

  const goToRoot = () => {
    setCurrentFolder(null);
    setFolderStack([]);
    setPage(1);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Step 1: Request upload URL
      setUploadProgress(25);

      const uploadUrlResponse = await axios.post(
        `/api/businesses/${locationId}/library`,
        {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          parent: currentFolder
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          timeout: 10000 // 10s timeout
        }
      );

      const { id: fileId, url: s3Url, fields } = uploadUrlResponse.data;
      setUploadProgress(50);

      // Step 2: Upload to S3 (use Fetch API to avoid axios Authorization header)
      setUploadProgress(75);

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(s3Url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      setUploadProgress(90);

      // Step 3: Confirm upload
      await axios.put(
        `/api/businesses/${locationId}/library/${fileId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          timeout: 10000 // 10s timeout
        }
      );

      setUploadProgress(100);

      // Refresh library and close dialog immediately
      setUploading(false);
      setUploadProgress(0);
      fetchLibrary();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      setError(`Upload failed: ${error.response?.data?.message || error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Separate folders and files, with folders first
  const folders = (library || []).filter(item => item.isFolder);
  const files = (library || []).filter(item => !item.isFolder);
  const allItems = [...folders, ...files];

  // Pagination
  const totalPages = Math.ceil(allItems.length / itemsPerPage);
  const startIdx = (page - 1) * itemsPerPage;
  const paginatedItems = allItems.slice(startIdx, startIdx + itemsPerPage);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Library
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {businessProfile?.businessName || 'Business'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            Upload File
          </Button>
        </Box>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept="*"
        />

        {/* Navigation Buttons */}
        {currentFolder !== null && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={goToRoot}
            >
               Back to Library
            </Button>
            {folderStack.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={goBack}
              >
                ← Back
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {!error && library && (
        <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
          <Chip
            icon={<FolderIcon />}
            label={`${folders.length} Folders`}
            variant="outlined"
            color="primary"
          />
          <Chip
            icon={<FileIcon />}
            label={`${files.length} Files`}
            variant="outlined"
            color="secondary"
          />
        </Box>
      )}

      {/* Library Grid */}
      {library && library.length > 0 ? (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {paginatedItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                    },
                  }}
                  onClick={() => {
                    if (item.isFolder) {
                      openFolder(item);
                    } else {
                      setSelectedItem(item);
                      setPreviewOpen(true);
                    }
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 200,
                      bgcolor: '#f5f5f5',
                      position: 'relative',
                    }}
                  >
                    {item.isFolder ? (
                      <FolderIcon sx={{ fontSize: 60, color: '#FF9800' }} />
                    ) : isConvertedPreviewFile(item.name, item.details?.extension) ? (
                      heicPreviewUrls[item._id] ? (
                        <Box
                          component="img"
                          src={heicPreviewUrls[item._id]}
                          alt={item.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                        />
                      ) : heicLoadingStates[item._id] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                          <CircularProgress size={24} />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <ImageIcon sx={{ fontSize: 40, color: '#2196f3', mb: 0.5 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#2196f3' }}>{isTiffFile(item.name, item.details?.extension) ? 'TIFF' : 'HEIC'}</Typography>
                          </Box>
                        </Box>
                      )
                    ) : item.details?.fileType?.startsWith('image/') ? (
                      <Box
                        component="img"
                        src={`/api/s3-image-proxy?fileKey=${encodeURIComponent(item.key)}`}
                        alt={item.name}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; width: 100%;"><svg style="font-size: 40px; color: #2196F3;">📷</svg></div>';
                        }}
                      />
                    ) : item.details?.fileType?.startsWith('video/') ? (
                      // Video thumbnail
                      videoPreviewUrls[item._id] ? (
                        <Box
                          component="img"
                          src={videoPreviewUrls[item._id]}
                          alt={item.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : videoLoadingStates[item._id] ? (
                        <CircularProgress size={40} />
                      ) : (
                        <VideoIcon sx={{ fontSize: 60, color: '#F44336' }} />
                      )
                    ) : (
                      getFileIcon(item)
                    )}
                    {!item.isFolder && (
                      <Chip
                        label={item.details?.extension || 'FILE'}
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>

                  {/* Content */}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name}
                    </Typography>

                    {item.isFolder ? (
                      <Chip
                        size="small"
                        label={`${item.totalFileCount} items`}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          {getFileStatus(item.name) && (
                            <Chip
                              label={getFileStatus(item.name)}
                              size="small"
                              sx={{
                                backgroundColor: getStatusColor(getFileStatus(item.name)),
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {formatFileSize(item.details?.fileSize)}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          {formatDate(item.createdAt)}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        !error && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CloudDownloadIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No files or folders in library
            </Typography>
          </Paper>
        )
      )}

      {/* Upload Progress Dialog */}
      <Dialog
        open={uploading}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        onClose={(e) => e.preventDefault()}
      >
        <DialogTitle>Uploading File</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
              {uploadProgress}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{selectedItem.name}</span>
              <IconButton onClick={() => setPreviewOpen(false)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {selectedItem.isFolder ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <FolderIcon sx={{ fontSize: 80, color: '#FF9800', mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Folder: {selectedItem.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Contains {selectedItem.totalFileCount} items
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  {isConvertedPreviewFile(selectedItem.name, selectedItem.details?.extension) ? (
                    heicPreviewUrls[selectedItem._id] ? (
                      <Box
                        component="img"
                        src={heicPreviewUrls[selectedItem._id]}
                        alt={selectedItem.name}
                        sx={{ maxWidth: '100%', maxHeight: 500 }}
                      />
                    ) : heicLoadingStates[selectedItem._id] ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <ImageIcon sx={{ fontSize: 80, color: '#2196f3', mb: 2 }} />
                        <Typography>{isTiffFile(selectedItem.name, selectedItem.details?.extension) ? 'TIFF' : 'HEIC'} Preview</Typography>
                      </Box>
                    )
                  ) : selectedItem.details?.fileType?.startsWith('image/') ? (
                    <Box
                      component="img"
                      src={`/api/s3-image-proxy?fileKey=${encodeURIComponent(selectedItem.key)}`}
                      alt={selectedItem.name}
                      sx={{ maxWidth: '100%', maxHeight: 500 }}
                      onError={(e) => {
                        console.error('❌ Image load failed:', selectedItem.key);
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        if (parent && !parent.querySelector('[data-error]')) {
                          const errorDiv = document.createElement('div');
                          errorDiv.setAttribute('data-error', 'true');
                          errorDiv.style.cssText = 'padding: 20px; color: #d32f2f; text-align: center;';
                          errorDiv.innerHTML = `<p>Image failed to load</p>`;
                          parent.appendChild(errorDiv);
                        }
                      }}
                    />
                  ) : selectedItem.details?.fileType?.startsWith('video/') ? (
                    <video
                      controls
                      style={{ maxWidth: '100%', maxHeight: 500 }}
                      src={`${selectedItem.url}/${selectedItem.key}`}
                    />
                  ) : (
                    <Box sx={{ py: 4 }}>
                      {getFileIcon(selectedItem)}
                      <Typography variant="body1" sx={{ mt: 2 }}>
                        {selectedItem.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedItem.details?.fileType}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ mt: 3, textAlign: 'left' }}>
                    <Typography variant="body2">
                      <strong>Size:</strong> {formatFileSize(selectedItem.details?.fileSize)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Type:</strong> {selectedItem.details?.fileType}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong> {formatDate(selectedItem.createdAt)}
                    </Typography>

                    {/* Status Section */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Status:</strong>
                      </Typography>
                      {statusEditing ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={selectedStatus || getFileStatus(selectedItem.name) || 'new'}
                              onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '50%',
                                        backgroundColor: option.color
                                      }}
                                    />
                                    {option.label}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => updateFileStatus(selectedItem.name, selectedStatus || getFileStatus(selectedItem.name))}
                            disabled={statusUpdating}
                            sx={{ backgroundColor: '#4caf50' }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setStatusEditing(false);
                              setSelectedStatus(null);
                            }}
                            disabled={statusUpdating}
                          >
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getFileStatus(selectedItem.name)}
                            size="small"
                            sx={{
                              backgroundColor: getStatusColor(getFileStatus(selectedItem.name)),
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setStatusEditing(true)}
                          >
                            Change
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

export default LibraryPage;
